/**
 * Collapse the table of contents on small screens.
 *
 * The Elementor widget this replaces was set to `minimize_box` with
 * `minimized_on: tablet`: open on a desktop, behind a chevron on a tablet or
 * phone. Eighteen entries is most of a phone screen, so without this the
 * article starts below the fold. Yoast's block has no collapse control, so the
 * theme adds one.
 *
 * Progressive enhancement on purpose: the markup ships expanded and with no
 * button, so a reader without JavaScript gets the whole list rather than a
 * heading that does nothing. The button and the collapsing only exist once this
 * runs.
 *
 * The breakpoint matches Elementor's tablet cutoff (1024px), so the behaviour
 * changes at the same width it used to.
 */

const BREAKPOINT = '(max-width: 1024px)';
const CHEVRON =
	'<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">' +
	'<path d="M5 7.5 10 12.5 15 7.5" stroke="currentColor" stroke-width="2" ' +
	'stroke-linecap="round" stroke-linejoin="round"/></svg>';

/**
 * Wire one table of contents.
 *
 * @param {HTMLElement} toc The block's wrapper.
 */
const enhance = (toc) => {
	const heading = toc.querySelector(':scope > h2');
	const list = toc.querySelector(':scope > ul');

	if (!heading || !list) {
		return;
	}

	if (!list.id) {
		list.id = `toc-list-${Math.random().toString(36).slice(2, 9)}`;
	}

	const button = document.createElement('button');
	button.type = 'button';
	button.className = 'yoast-table-of-contents__toggle';
	button.setAttribute('aria-controls', list.id);
	button.innerHTML = CHEVRON;

	const narrow = window.matchMedia(BREAKPOINT);

	/**
	 * Open or close, keeping the button's label and state in step.
	 *
	 * @param {boolean} open Whether the list should be visible.
	 */
	const setOpen = (open) => {
		list.hidden = !open;
		button.setAttribute('aria-expanded', String(open));
		button.setAttribute('aria-label', open ? 'Hide the table of contents' : 'Show the table of contents');
	};

	/**
	 * Collapsing only applies on narrow screens. Widening the window reopens
	 * the list rather than leaving it shut with the control hidden, which is
	 * how a reader ends up with a heading and nothing under it.
	 */
	const apply = () => {
		toc.classList.toggle('yoast-table-of-contents--collapsible', narrow.matches);
		setOpen(!narrow.matches);
	};

	button.addEventListener('click', () => setOpen(list.hidden));
	narrow.addEventListener('change', apply);

	toc.appendChild(button);
	apply();
};

const start = () => document.querySelectorAll('.yoast-table-of-contents').forEach(enhance);

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', start);
} else {
	start();
}
